import {
  DropdownOption,
  FieldHandler,
  FieldHandlerValidator,
} from '../auxiliary-data-table/auxiliary-data-table.interface';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { FieldDataUpsertOperationInterface } from '@vendasta/account-group';
import { OnInit, ViewContainerRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Component, NgModule } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { ReadOnlyStyles } from './common';

export class DropdownFieldHandler implements FieldHandler {
  description: string;
  formControl: UntypedFormControl;

  constructor(
    public fieldId: string,
    public name: string,
    public label: string,
    public value: string,
    private dropdownOptions: DropdownOption[],
    public validators: FieldHandlerValidator[],
    public readOnly = false,
  ) {
    this.formControl = new UntypedFormControl(value);
    this.formControl.setValidators(validators.map((v) => v.validatorFn));
  }

  render(viewContainerRef: ViewContainerRef): void {
    viewContainerRef.clear();
    const component = viewContainerRef.createComponent<DropdownRendererComponent>(DropdownRendererComponent);
    component.instance.control = this.formControl;
    component.instance.label = this.label;
    component.instance.dropdownOptions = this.dropdownOptions;
    component.instance.validators = this.validators;
    component.instance.readOnly = this.readOnly;
  }

  hasModification(): boolean {
    return this.formControl?.dirty;
  }

  upsertOperation(): FieldDataUpsertOperationInterface {
    return {
      fieldDataId: this.fieldId,
      dropdownValue: this.formControl.value,
    };
  }
}

@Component({
  styles: [
    `
      :host,
      mat-form-field,
      input {
        width: 100%;
      }

      :host ::ng-deep .hide-error-padding .mat-form-field-wrapper {
        padding-bottom: 0;
      }
      :host ::ng-deep .mat-form-field-appearance-outline .mat-form-field-flex {
        margin-top: 0;
      }
      #dropdown {
        margin: 0 5px;
        padding: 6px 0 8px;
      }
      #dropdown ::ng-deep .input-and-hints {
        margin: 0;
      }
    `,
    ...ReadOnlyStyles,
  ],
  template: `
    <div *ngIf="readOnly; else formField">
      <div class="label">
        {{ label }}
      </div>
      <div class="value">
        {{ dropdownOptionMap.get(control.value) ? dropdownOptionMap.get(control.value) : '-' }}
      </div>
    </div>
    <ng-template #formField>
      <glxy-form-field [size]="'large'" id="dropdown">
        <glxy-label *ngIf="label">{{ label }}</glxy-label>
        <mat-select [(formControl)]="control">
          <!-- let people un-set dropdown -->
          <mat-option class="unset-dropdown" [value]="''">--</mat-option>

          <mat-option *ngFor="let dropdownOption of dropdownOptions" [value]="dropdownOption.value">
            {{ dropdownOption.label }}
          </mat-option>
        </mat-select>
        <ng-container *ngFor="let validator of validators" ngProjectAs="mat-error">
          <glxy-error *ngIf="control.hasError(validator.errorCode)">
            {{ validator.errorMessage | translate }}
          </glxy-error>
        </ng-container>
      </glxy-form-field>
    </ng-template>
  `,
  standalone: false,
})
export class DropdownRendererComponent implements OnInit {
  control!: UntypedFormControl;
  label!: string;
  dropdownOptions!: DropdownOption[];
  validators!: FieldHandlerValidator[];
  readOnly!: boolean;

  dropdownOptionMap: Map<string, string> = new Map();
  invalidOption: DropdownOption;

  ngOnInit(): void {
    const currentValue = this.control?.value;
    if (currentValue && !this.dropdownOptions?.map((d) => d.value).includes(currentValue)) {
      this.invalidOption = {
        value: currentValue,
        label: currentValue,
      };
      this.dropdownOptions.push(this.invalidOption);
    }
    this.dropdownOptions.forEach((d) => {
      this.dropdownOptionMap.set(d.value, d.label);
    });
    this.setInvalidOptionValidator();
  }

  private setInvalidOptionValidator(): void {
    const invalidOptionValidator = {
      errorCode: 'invalid',
      errorMessage: 'COMMON.INVALID_DROPDOWN_ERROR',
      validatorFn: (control) => {
        if (this.invalidOption?.value === control.value) {
          return { invalid: true };
        }
        return null;
      },
    };

    this.validators?.push(invalidOptionValidator);
    this.control.addValidators(invalidOptionValidator.validatorFn);
  }
}

@NgModule({
  imports: [CommonModule, MatSelectModule, ReactiveFormsModule, TranslateModule, GalaxyFormFieldModule],
  declarations: [DropdownRendererComponent],
  exports: [DropdownRendererComponent],
})
export class DropdownRenderComponentModule {}
